import apiClient from './api';
import { ApiResponse, PaginationMeta } from '../types';

export interface GraphKnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  type: string;
}

export interface GraphNode {
  id: string;
  organization_id: string;
  knowledge_base_id: string;
  name: string;
  node_type: string;
  summary: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface GraphEdge {
  id: string;
  source_node_id: string;
  target_node_id: string;
  relationship_type: string;
  weight: number;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface GraphAgent {
  id: string;
  name: string;
  type: string;
  status: string;
}

export interface AgentKBLink {
  agent_id: string;
  knowledge_base_id: string;
}

export interface GraphChannel {
  id: string;
  name: string;
  channel_type: string;
  status: string;
}

export interface ChannelAgentLink {
  channel_id: string;
  agent_id: string;
}

export interface OrganizationGraph {
  knowledge_bases: GraphKnowledgeBase[];
  nodes: GraphNode[];
  edges: GraphEdge[];
  agents: GraphAgent[];
  agent_kb_links: AgentKBLink[];
  channels: GraphChannel[];
  channel_agent_links: ChannelAgentLink[];
  stats: {
    total_nodes: number;
    total_edges: number;
    total_kbs: number;
    total_agents: number;
    total_channels: number;
  };
}

export interface KBNodeCountStat {
  kb_id: string;
  node_count: number;
}

export interface GraphHierarchy {
  knowledge_bases: GraphKnowledgeBase[];
  agents: GraphAgent[];
  agent_kb_links: AgentKBLink[];
  channels: GraphChannel[];
  channel_agent_links: ChannelAgentLink[];
  stats_per_kb: KBNodeCountStat[];
  stats: {
    total_kbs: number;
    total_agents: number;
    total_channels: number;
  };
}

export interface KBNodeListItem {
  id: string;
  name: string;
  type: string;
  summary: string | null;
  connection_count: number;
}

export interface KBGraphEdge {
  id: string;
  source_node_id: string;
  target_node_id: string;
  relationship_type: string;
  weight: number;
}

export interface NodeDetail {
  node: GraphNode;
  connected_nodes: GraphNode[];
  edges: GraphEdge[];
  chunk_count: number;
}

class GraphService {
  async getOrganizationGraph(): Promise<OrganizationGraph> {
    const response = await apiClient.get<ApiResponse<OrganizationGraph>>('/knowledge-graph');
    if (response.data.data) return response.data.data;
    throw new Error('Failed to fetch organization graph');
  }

  async buildGraphs(): Promise<{ results: Array<{ kb_id: string; kb_name: string; status: string }> }> {
    const response = await apiClient.post<ApiResponse<{ results: Array<{ kb_id: string; kb_name: string; status: string }> }>>('/knowledge-graph/build');
    if (response.data.data) return response.data.data;
    throw new Error('Failed to build graphs');
  }

  async getNodeDetail(nodeId: string): Promise<NodeDetail> {
    const response = await apiClient.get<ApiResponse<NodeDetail>>(`/knowledge-graph/${nodeId}`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to fetch node detail');
  }

  async getHierarchy(): Promise<GraphHierarchy> {
    const response = await apiClient.get<ApiResponse<GraphHierarchy>>('/knowledge-graph/hierarchy');
    if (response.data.data) return response.data.data;
    throw new Error('Failed to fetch graph hierarchy');
  }

  async getKBNodes(
    kbId: string,
    params?: { page?: number; limit?: number; search?: string }
  ): Promise<{ data: KBNodeListItem[]; pagination: PaginationMeta }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);

    const qs = query.toString();
    const response = await apiClient.get<ApiResponse<{ nodes: KBNodeListItem[] }>>(
      `/knowledge-graph/kb/${kbId}/nodes${qs ? '?' + qs : ''}`
    );
    const payload = response.data.data;
    return {
      data: payload?.nodes || [],
      pagination: response.data.meta || { totalItems: 0, totalPages: 0, perPage: 20, currentPage: 1 },
    };
  }

  async getKBGraphNodes(
    kbId: string,
    limit = 30
  ): Promise<{ nodes: KBNodeListItem[]; edges: KBGraphEdge[]; total: number }> {
    const response = await apiClient.get<ApiResponse<{ nodes: KBNodeListItem[]; edges: KBGraphEdge[] }>>(
      `/knowledge-graph/kb/${kbId}/nodes?limit=${limit}&include_edges=true`
    );
    const payload = response.data.data;
    return {
      nodes: payload?.nodes || [],
      edges: payload?.edges || [],
      total: response.data.meta?.totalItems || 0,
    };
  }
}

export const graphService = new GraphService();
